// =============================================================================
// slotCalculation.ts — pure TS utility for slot preview (no network)
// =============================================================================
//
// Used exclusively by the schedule editor (/settings/instructor-tools) to give
// the instructor immediate visual feedback while editing intervals — without
// any round-trip to the database.
//
// This is NOT the source of truth for real slot availability.
// Real availability (conflicts, approved requests, etc.) lives in the SQL
// function calculate_instructor_available_slots() — see ADR-0001.
// =============================================================================

export interface ScheduleInterval {
  day_of_week: string;
  start_time: string; // "HH:MM"
  end_time: string;   // "HH:MM"
}

export interface PreviewSlot {
  startTime: string;    // "HH:MM"
  endTime: string;      // "HH:MM"
  durationMinutes: number;
}

/**
 * Generates preview slots for a given day from a list of schedule intervals.
 *
 * Pure function — no side effects, no network calls, no conflict checking.
 * Walks each interval in `durationMin`-sized steps and emits a slot per step.
 *
 * @param intervals   - Array of schedule intervals (may span multiple days)
 * @param dayOfWeek   - Three-letter key: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'
 * @param durationMin - Slot size in minutes (e.g. 60)
 * @returns Array of preview slots, ordered by start time
 */
export function previewSlotsFromIntervals(
  intervals: ScheduleInterval[],
  dayOfWeek: string,
  durationMin: number,
): PreviewSlot[] {
  if (!intervals.length || durationMin <= 0) return [];

  const dayIntervals = intervals
    .filter(iv => iv.day_of_week === dayOfWeek)
    .sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));

  const slots: PreviewSlot[] = [];

  for (const interval of dayIntervals) {
    const intervalStart = timeToMinutes(interval.start_time);
    const intervalEnd   = timeToMinutes(interval.end_time);

    let cursor = intervalStart;
    while (cursor + durationMin <= intervalEnd) {
      slots.push({
        startTime:     minutesToTime(cursor),
        endTime:       minutesToTime(cursor + durationMin),
        durationMinutes: durationMin,
      });
      cursor += durationMin;
    }
  }

  return slots;
}

// ── helpers ──────────────────────────────────────────────────────────────────

/** "HH:MM" → total minutes since midnight */
function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/** Total minutes since midnight → "HH:MM" */
function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
